import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LinkSimple } from "@phosphor-icons/react";
import { ByproductPanel } from "@/components/routing/ByproductPanel";

const selectClass =
  "h-8 rounded-md border border-[#D0D5DD] bg-white px-2 text-[12px] outline-none focus:border-[#0E7C86] focus:ring-1 focus:ring-[#0E7C86] transition-colors";

export const LinkPanel = ({
  details, context, mappings, onMapping, byproductPlan, acknowledged, onAcknowledge,
  onOverride, onLink, linking, modelId,
}) => {
  if (!details) return null;
  const operations = details.operations || [];
  const production = operations.filter((op) => !op.is_inspection);
  const blocked = !details.consistent || linking
    || (byproductPlan?.length > 0 && !acknowledged);

  return (
    <div className="space-y-3" data-testid="routing-link-panel">
      <div className="rounded-lg border border-[#D0D5DD] bg-white p-3" data-testid="routing-link-operations">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[13px] font-semibold text-[#1D2939]">{details.boo_id}</span>
          <Badge
            variant="outline"
            className={`text-[10px] ${
              details.consistent
                ? "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]"
                : "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]"
            }`}
          >
            {details.planning_consistency_label} / {details.execution_consistency_label}
          </Badge>
          {details.site_id && <span className="text-[11px] text-[#667085]">site {details.site_id}</span>}
        </div>
        <div className="mt-2 space-y-1">
          {operations.map((op) => (
            <div key={op.element_id} className="flex flex-wrap items-center gap-2 text-[12px]">
              <span className="font-mono text-[#475467] w-16 shrink-0">{op.element_id}</span>
              <span className="text-[#1D2939] truncate max-w-[280px]">{op.description}</span>
              <span className="text-[11px] text-[#98A2B3]">
                {op.operation_type_label} · activity {op.activity_id} · {op.resource_id || "no resource"}
                {op.duration_minutes ? ` · ${op.duration_minutes} min` : ""}
              </span>
            </div>
          ))}
        </div>
        {!details.consistent && (
          <p className="mt-2 text-[12px] text-[#B42318]" data-testid="routing-link-inconsistent">
            SAP reports this routing as inconsistent, so it cannot be linked - a flagged routing is never allowed
            to replace a working one.
          </p>
        )}
      </div>

      <div>
        <div className="text-[11px] font-semibold text-[#475467] uppercase tracking-wide mb-1.5">
          Where each component is backflushed
        </div>
        <div className="rounded-lg border border-[#D0D5DD] bg-white divide-y divide-[#F2F4F7]">
          {!(context?.item_groups || []).length && (
            <p className="px-3 py-2 text-[12px] text-[#667085]">
              This bill of material has no components to assign.
            </p>
          )}
          {(context?.item_groups || []).map((group) => (
            <div
              key={group.item_group_id}
              className="flex flex-wrap items-center gap-2 px-3 py-2"
              data-testid={`routing-link-mapping-row-${group.item_group_id}`}
            >
              <span className="text-[12px] text-[#1D2939] flex-1 min-w-0 truncate">{group.label}</span>
              <select
                value={mappings[group.item_group_id] || ""}
                onChange={(e) => onMapping(group.item_group_id, e.target.value)}
                data-testid={`routing-link-mapping-${group.item_group_id}`}
                className={selectClass}
              >
                <option value="">Not assigned</option>
                {production.map((op) => (
                  <option key={op.element_id} value={op.element_id}>
                    {`${op.element_id} - ${op.description.slice(0, 32)}`}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
        <p className="mt-1 text-[11px] text-[#667085]">
          Anything left unassigned is consumed by SAP at the first production step.
        </p>
      </div>

      <ByproductPanel
        plan={byproductPlan}
        options={production.map((op) => ({ value: op.element_id, label: `${op.element_id} - ${op.description.slice(0, 40)}` }))}
        acknowledged={acknowledged}
        onAcknowledge={onAcknowledge}
        onOverride={onOverride}
      />

      <Button
        onClick={onLink}
        disabled={blocked}
        data-testid="routing-link-button"
        className="h-9 bg-[#0E7C86] hover:bg-[#0B656D] text-white text-[13px] disabled:opacity-50"
      >
        <LinkSimple size={15} className="mr-1.5" />
        {linking ? "Linking in SAP…" : `Link ${details.boo_id} to ${modelId}`}
      </Button>
    </div>
  );
};
